import React, { useEffect, useState } from "react";
import { ShieldCheck, Users, FileText, AlertTriangle, Truck, Award, Database, Sparkles } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  ShieldCheck, Users, FileText, AlertTriangle, Truck, Award, Database,
};

const TONE = {
  state_license:    { grad: "from-dox-orange to-dox-magenta",   ring: "border-dox-orange/30" },
  workers_comp:     { grad: "from-emerald-400 to-sky-500",      ring: "border-emerald-400/25" },
  insurance_bond:   { grad: "from-sky-400 to-violet-500",       ring: "border-sky-400/25" },
  osha:             { grad: "from-amber-400 to-dox-orange",     ring: "border-amber-400/25" },
  dot:              { grad: "from-dox-purple to-dox-magenta",   ring: "border-dox-purple/25" },
  safety_cert:      { grad: "from-rose-400 to-dox-magenta",     ring: "border-rose-400/25" },
  tax_registration: { grad: "from-violet-400 to-dox-purple",    ring: "border-violet-400/25" },
};

export default function ComplianceModules() {
  const [modules, setModules] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/compliance-modules");
        setModules(data.modules || []);
      } catch { setModules([]); }
    })();
  }, []);

  if (modules === null) return <div className="h-48 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="compliance-modules-view">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-dox-orange" />
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-dox-magenta">Dox&apos;s Practice Areas</div>
        </div>
        <h2 className="font-display font-black text-3xl">Every kind of compliance work Dox knows how to do.</h2>
        <p className="text-white/60 mt-3 max-w-2xl">
          When you send Dox a document, it&apos;s automatically classified into one of these modules and routed to the right agency and cadence — so the renewal packet Dox prepares is tuned to that filing type.
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {modules.map((m) => {
          const Icon = ICONS[m.icon] || ShieldCheck;
          const tone = TONE[m.id] || { grad: "from-white/20 to-white/5", ring: "border-white/10" };
          return (
            <div key={m.id} className={`glass-card p-6 ${tone.ring}`} data-testid={`module-${m.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${tone.grad} flex items-center justify-center shadow-[0_0_18px_rgba(30,120,255,0.25)]`}>
                  <Icon size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold text-white leading-tight">{m.name}</div>
                  <div className="text-[10px] tracking-widest uppercase text-white/40 mt-0.5">Renews every {m.renewal_cadence_months} months</div>
                </div>
              </div>
              <div className="text-xs text-white/50 leading-relaxed">
                <div className="text-white/70">{m.agency_default}</div>
                <div className="mt-2 line-clamp-3">{m.package_intro}</div>
              </div>
              {m.required_fields?.length ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {m.required_fields.slice(0, 5).map((f) => (
                    <span key={f} className="text-[10px] rounded-full glass border border-white/10 px-2 py-0.5 text-white/60">{f.replace(/_/g, " ")}</span>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Small pill for showing which module a document belongs to. */
const MODULE_LABELS = {
  state_license: "State License",
  workers_comp: "Workers' Comp",
  insurance_bond: "Liability & Bonds",
  osha: "OSHA",
  dot: "DOT",
  safety_cert: "Safety Cert",
  tax_registration: "Tax & Registration",
};

export function ModuleBadge({ moduleId, moduleName }) {
  if (!moduleId) return null;
  const tone = TONE[moduleId] || { grad: "from-white/20 to-white/5" };
  const label = moduleName || MODULE_LABELS[moduleId] || moduleId;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full glass-strong border border-white/10 px-2.5 py-1 text-[11px] font-semibold`}
      data-testid={`module-badge-${moduleId}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${tone.grad}`} />
      <span className="text-white/85">{label}</span>
    </span>
  );
}
